import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';

const PermissionsDoc = graphql(/* GraphQL */ `
  query PermissionCatalog {
    permissions {
      code
      resource
      action
      description
      category
      requiresStrongAuth
    }
  }
`);

const RoleDoc = graphql(/* GraphQL */ `
  query RoleDetail($id: UUID!) {
    role(id: $id) {
      id
      name
      slug
      description
      actorKind
      isSystem
      permissions {
        code
      }
    }
  }
`);

const CreateRoleDoc = graphql(/* GraphQL */ `
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      id
    }
  }
`);

const UpdateRoleDoc = graphql(/* GraphQL */ `
  mutation UpdateRole($id: UUID!, $input: UpdateRoleInput!) {
    updateRole(id: $id, input: $input) {
      id
    }
  }
`);

const DeleteRoleDoc = graphql(/* GraphQL */ `
  mutation DeleteRole($id: UUID!) {
    deleteRole(id: $id)
  }
`);

export function RoleEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const { data: permData } = useQuery(PermissionsDoc);
  const { data: roleData, loading } = useQuery(RoleDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
  });

  const role = roleData?.role ?? null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actorKind, setActorKind] = useState<'USER' | 'DEVICE' | 'ANY'>('USER');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (role && role.id !== seededFor) {
    setSeededFor(role.id);
    setName(role.name);
    setDescription(role.description);
    setActorKind(role.actorKind as 'USER' | 'DEVICE' | 'ANY');
    setSelected(new Set(role.permissions.map((p) => p.code)));
  }

  const byCategory = useMemo(() => {
    const map = new Map<
      string,
      { code: string; description: string; requiresStrongAuth: boolean }[]
    >();
    for (const p of permData?.permissions ?? []) {
      const list = map.get(p.category) ?? [];
      list.push({
        code: p.code,
        description: p.description,
        requiresStrongAuth: p.requiresStrongAuth,
      });
      map.set(p.category, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permData]);

  const [createRole, { loading: creating }] = useMutation(CreateRoleDoc);
  const [updateRole, { loading: updating }] = useMutation(UpdateRoleDoc);
  const [deleteRole, { loading: deleting }] = useMutation(DeleteRoleDoc);

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const save = async () => {
    const codes = [...selected];
    if (isNew) {
      const res = await createRole({
        variables: {
          input: {
            name,
            description: description || null,
            actorKind,
            permissionCodes: codes,
          },
        },
      });
      const newId = res.data?.createRole.id;
      if (newId) navigate(`/roles/${newId}`, { replace: true });
      return;
    }
    await updateRole({
      variables: {
        id: id!,
        input: { name, description, permissionCodes: codes },
      },
    });
  };

  const onDelete = async () => {
    if (!id || role?.isSystem) return;
    if (!window.confirm('Delete this role?')) return;
    await deleteRole({ variables: { id } });
    navigate('/roles');
  };

  if (!isNew && loading) {
    return <p className="text-sm text-gray-500">Loading…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          {isNew ? 'New role' : role?.name ?? 'Role'}
        </h1>
        <div className="flex gap-2">
          {!isNew && !role?.isSystem ? (
            <Button
              variant="danger"
              onClick={onDelete}
              disabled={deleting}
            >
              Delete
            </Button>
          ) : null}
          <Button onClick={save} disabled={creating || updating || !name.trim()}>
            Save
          </Button>
        </div>
      </div>

      <FormLayout>
        <FormSection title="Details">
          <FormField label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={role?.isSystem === true && !isNew}
            />
          </FormField>
          <FormField label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>
          {isNew ? (
            <FormField label="Actor kind">
              <Select
                value={actorKind}
                onChange={(e) =>
                  setActorKind(e.target.value as 'USER' | 'DEVICE' | 'ANY')
                }
                options={[
                  { value: 'USER', label: 'User' },
                  { value: 'DEVICE', label: 'Device' },
                  { value: 'ANY', label: 'Any' },
                ]}
              />
            </FormField>
          ) : null}
        </FormSection>

        <FormSection
          title="Permissions"
          description="A Passkey badge means GraphQL rejects the mutation unless the session AMR is passkey (Deliverable 1.5). Magic-link sessions are refused."
        >
          <div className="space-y-6">
            {byCategory.map(([category, perms]) => (
              <div key={category}>
                <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {category}
                </h3>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {perms.map((p) => (
                    <label
                      key={p.code}
                      className="flex items-start gap-2 rounded border border-gray-200 p-2 text-sm dark:border-gray-700"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={selected.has(p.code)}
                        onChange={() => toggle(p.code)}
                        disabled={role?.slug === 'super_admin'}
                      />
                      <span>
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="font-mono text-xs">{p.code}</span>
                          {p.requiresStrongAuth ? (
                            <Badge tone="warning">Passkey</Badge>
                          ) : null}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {p.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </FormSection>
      </FormLayout>
    </div>
  );
}
